import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import ResetPassword from './components/ResetPassword';
import OAuthSuccess from './components/OAuthSuccess';
import { post, setCsrfToken, clearCsrfToken } from './utils/api';
import { getApiUrl } from './config/api';

const routerBasename = process.env.NODE_ENV === 'development' ? '/' : '/OnlineMarriageLicense';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in by verifying cookie
    const checkAuth = async () => {
      const savedUser = localStorage.getItem('user');
      const isLocalPreview =
        process.env.NODE_ENV === 'development' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

      if (isLocalPreview && localStorage.getItem('theme_preview_auth') === '1') {
        const previewUser = savedUser
          ? JSON.parse(savedUser)
          : {
              id: 'theme-preview',
              email: 'preview@example.com',
              firstName: 'Theme',
              lastName: 'Preview',
              name: 'Theme Preview'
            };

        localStorage.setItem('user', JSON.stringify(previewUser));
        setIsAuthenticated(true);
        setUser(previewUser);
        setLoading(false);
        return;
      }

      // Quick check: if we have user data, try to verify the session
      if (savedUser) {
        try {
          const response = await fetch(getApiUrl('/api/auth/me'), {
            credentials: 'include' // Send httpOnly cookie
          });

          if (response.ok) {
            const data = await response.json();

            // Store CSRF token if provided
            if (data.csrfToken) {
              setCsrfToken(data.csrfToken);
            }

            setIsAuthenticated(true);
            setUser(data.user);
            // Update localStorage with fresh user data
            localStorage.setItem('user', JSON.stringify(data.user));
          } else {
            // Cookie expired or invalid, clear localStorage and CSRF token
            localStorage.removeItem('user');
            clearCsrfToken();
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('user');
          clearCsrfToken();
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleAuthSuccess = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      // Call logout endpoint to clear cookie (requires CSRF token)
      await post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state and CSRF token regardless
      localStorage.removeItem('user');
      clearCsrfToken();
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader-lg"></div>
      </div>
    );
  }

  const defaultPath = isAuthenticated ? '/dashboard' : '/login';

  return (
    <Router basename={routerBasename} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="App">
          <Routes>
            <Route
              path="/login"
              element={
                isAuthenticated ?
                  <Navigate to="/dashboard" replace /> :
                  <Auth onAuthSuccess={handleAuthSuccess} />
              }
            />
            <Route
              path="/auth"
              element={
                isAuthenticated ?
                  <Navigate to="/dashboard" replace /> :
                  <Auth onAuthSuccess={handleAuthSuccess} />
              }
            />
            <Route
              path="/auth/oauth-success"
              element={<OAuthSuccess onAuthSuccess={handleAuthSuccess} />}
            />
            <Route
              path="/dashboard"
              element={
                isAuthenticated ?
                  <Dashboard user={user} onLogout={handleLogout} /> :
                  <Navigate to="/login" replace />
              }
            />
            <Route
              path="/reset-password/:token"
              element={<ResetPassword />}
            />
            <Route path="/" element={<Navigate to={defaultPath} replace />} />
            <Route path="/index.html" element={<Navigate to={defaultPath} replace />} />
            <Route path="*" element={<Navigate to={defaultPath} replace />} />
          </Routes>
        </div>
      </Router>
  );
}

export default App;
